<template>
  <div class="tiptap-editor">
    <div v-if="editor" class="editor-toolbar">
      <button type="button" @click="editor.chain().focus().undo().run()" :disabled="!editor.can().undo()" class="toolbar-btn" title="Undo">
        <i class="fa fa-undo"></i>
      </button>
      <button type="button" @click="editor.chain().focus().redo().run()" :disabled="!editor.can().redo()" class="toolbar-btn" title="Redo">
        <i class="fa fa-repeat"></i>
      </button>
      <span class="toolbar-divider"></span>
      <select @change="onFormatChange" class="toolbar-select" :value="getCurrentFormat()">
        <option value="paragraph">Paragraph</option>
        <option value="heading1">Heading 1</option>
        <option value="heading2">Heading 2</option>
        <option value="heading3">Heading 3</option>
      </select>
      <span class="toolbar-divider"></span>
      <button type="button" @click="editor.chain().focus().toggleBold().run()" :class="{ 'is-active': editor.isActive('bold') }" class="toolbar-btn" title="Bold">
        <i class="fa fa-bold"></i>
      </button>
      <button type="button" @click="editor.chain().focus().toggleItalic().run()" :class="{ 'is-active': editor.isActive('italic') }" class="toolbar-btn" title="Italic">
        <i class="fa fa-italic"></i>
      </button>
      <button type="button" @click="editor.chain().focus().toggleUnderline().run()" :class="{ 'is-active': editor.isActive('underline') }" class="toolbar-btn" title="Underline">
        <i class="fa fa-underline"></i>
      </button>
      <button type="button" @click="editor.chain().focus().toggleStrike().run()" :class="{ 'is-active': editor.isActive('strike') }" class="toolbar-btn" title="Strikethrough">
        <i class="fa fa-strikethrough"></i>
      </button>
      <span class="toolbar-divider"></span>
      <button type="button" @click="editor.chain().focus().setTextAlign('left').run()" :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }" class="toolbar-btn" title="Align Left">
        <i class="fa fa-align-left"></i>
      </button>
      <button type="button" @click="editor.chain().focus().setTextAlign('center').run()" :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }" class="toolbar-btn" title="Align Center">
        <i class="fa fa-align-center"></i>
      </button>
      <button type="button" @click="editor.chain().focus().setTextAlign('right').run()" :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }" class="toolbar-btn" title="Align Right">
        <i class="fa fa-align-right"></i>
      </button>
      <button type="button" @click="editor.chain().focus().setTextAlign('justify').run()" :class="{ 'is-active': editor.isActive({ textAlign: 'justify' }) }" class="toolbar-btn" title="Justify">
        <i class="fa fa-align-justify"></i>
      </button>
      <span class="toolbar-divider"></span>
      <button type="button" @click="editor.chain().focus().toggleBulletList().run()" :class="{ 'is-active': editor.isActive('bulletList') }" class="toolbar-btn" title="Bullet List">
        <i class="fa fa-list-ul"></i>
      </button>
      <button type="button" @click="editor.chain().focus().toggleOrderedList().run()" :class="{ 'is-active': editor.isActive('orderedList') }" class="toolbar-btn" title="Numbered List">
        <i class="fa fa-list-ol"></i>
      </button>
      <span class="toolbar-divider"></span>
      <button type="button" @click="openLinkModal" :class="{ 'is-active': editor.isActive('link') }" class="toolbar-btn" title="Insert Link">
        <i class="fa fa-link"></i>
      </button>
      <button type="button" @click="editor.chain().focus().unsetLink().run()" :disabled="!editor.isActive('link')" class="toolbar-btn" title="Remove Link">
        <i class="fa fa-unlink"></i>
      </button>
      <span class="toolbar-divider"></span>
      <button type="button" @click="openImageModal" class="toolbar-btn" title="Insert Image">
        <i class="fa fa-image"></i>
      </button>
      <span class="toolbar-divider"></span>
      <input type="color" @input="editor.chain().focus().setColor($event.target.value).run()" :value="editor.getAttributes('textStyle').color || '#000000'" class="toolbar-color-picker" title="Text Color" />
      <input type="color" @input="editor.chain().focus().toggleHighlight({ color: $event.target.value }).run()" class="toolbar-color-picker" title="Background Color" />
      <span class="toolbar-divider"></span>
      <button type="button" @click="editor.chain().focus().unsetAllMarks().run()" class="toolbar-btn" title="Clear Formatting">
        <i class="fa fa-eraser"></i>
      </button>
    </div>

    <!-- Link Modal -->
    <div v-if="showLinkModal" class="custom-modal-overlay" @click="closeLinkModal" role="dialog" aria-labelledby="link-modal-title" aria-modal="true">
      <div class="custom-modal" @click.stop>
        <div class="custom-modal-header">
          <h5 id="link-modal-title">Insert Link</h5>
          <button type="button" class="close-btn" @click="closeLinkModal" aria-label="Close">&times;</button>
        </div>
        <div class="custom-modal-body">
          <form @submit.prevent="insertLink">
            <div class="form-group">
              <label for="link-url-input">URL</label>
              <input 
                type="url" 
                id="link-url-input"
                class="form-control" 
                v-model="linkUrl" 
                placeholder="https://example.com" 
                @keydown.enter.prevent="insertLink" 
                @keyup.enter.prevent="insertLink"
                autofocus
              />
              <span v-if="linkError" class="error-message">{{ linkError }}</span>
            </div>
          </form>
        </div>
        <div class="custom-modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeLinkModal">Cancel</button>
          <button type="button" class="btn btn-primary" @click="insertLink">Insert</button>
        </div>
      </div>
    </div>

    <!-- Image Modal -->
    <div v-if="showImageModal" class="custom-modal-overlay" @click="closeImageModal" role="dialog" aria-labelledby="image-modal-title" aria-modal="true">
      <div class="custom-modal" @click.stop>
        <div class="custom-modal-header">
          <h5 id="image-modal-title">Insert Image</h5>
          <button type="button" class="close-btn" @click="closeImageModal" aria-label="Close">&times;</button>
        </div>
        <div class="custom-modal-body">
          <form @submit.prevent="insertImage">
            <div class="form-group">
              <label for="image-url-input">Image URL</label>
              <input 
                type="url" 
                id="image-url-input"
                class="form-control" 
                v-model="imageUrl" 
                placeholder="https://example.com/image.jpg" 
                @keydown.enter.prevent="insertImage" 
                @keyup.enter.prevent="insertImage"
                required
                autofocus
              />
              <span v-if="imageError" class="error-message">{{ imageError }}</span>
            </div>
          </form>
        </div>
        <div class="custom-modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeImageModal">Cancel</button>
          <button type="button" class="btn btn-primary" @click="insertImage">Insert</button>
        </div>
      </div>
    </div>

    <editor-content :editor="editor" class="editor-content" />
    <div v-if="editor" class="character-count">
      {{ characterCount }} characters
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Paragraph from "@tiptap/extension-paragraph";
import { Extension } from "@tiptap/core";

// Custom extension for Word-like Enter behavior
const WordLikeEnter = Extension.create({
  name: 'wordLikeEnter',
  
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        const { $from } = selection;
        
        // Check if we're in a list
        const inList = $from.parent.type.name === 'listItem' || $from.parent.type.name === 'taskItem';
        
        if (inList) {
          // Check if it's an empty list item (should exit the list like Word)
          const nodeAfter = $from.nodeAfter;
          const isLastItem = $from.indexAfter($from.depth) === $from.parent.childCount;
          
          if (nodeAfter === null && isLastItem) {
            // Exit the list
            return editor.chain().focus().liftListItem('listItem').run();
          }
          
          // Continue with default list behavior
          return false;
        }
        
        // Not in a list - return false to let StarterKit handle it
        // This will create a new paragraph and reset formatting via keepMarks: false
        return false;
      },
      
      'Shift-Enter': ({ editor }) => {
        // Soft line break within the same paragraph (Word behavior)
        return editor.chain().focus().setHardBreak().run();
      },
    };
  },
});

export default {
  name: "TiptapEditor",
  components: {
    EditorContent,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      editor: null,
      showLinkModal: false,
      showImageModal: false,
      linkUrl: "",
      imageUrl: "",
      linkError: "",
      imageError: "",
      characterCount: 0,
    };
  },
  mounted() {
    this.editor = new Editor({
      content: this.value,
      extensions: [
        WordLikeEnter,
        StarterKit.configure({
          paragraph: {
            HTMLAttributes: {
              class: 'tiptap-paragraph',
            },
          },
          heading: {
            levels: [1, 2, 3],
            allowMarks: true,
          },
          bulletList: {
            keepMarks: false,
            keepAttributes: false,
          },
          orderedList: {
            keepMarks: false,
            keepAttributes: false,
          },
          listItem: {
            keepMarks: false,
            keepAttributes: false,
          },
          blockquote: {
            allowMarks: true,
          },
        }),
        Underline,
        TextStyle,
        Color,
        Highlight.configure({ 
          multicolor: true,
        }),
        Link.configure({
          openOnClick: false,
          linkOnPaste: true,
          autolink: true,
          HTMLAttributes: {
            target: "_blank",
            rel: "noopener noreferrer",
          },
        }),
        TextAlign.configure({
          types: ["heading", "paragraph"],
          defaultAlignment: "left",
        }),
        Image.configure({
          inline: true,
          allowBase64: true,
        }),
      ],
      onUpdate: () => {
        this.$emit("input", this.editor.getHTML());
        this.updateCharacterCount();
      },
    });
    this.updateCharacterCount();
    document.addEventListener("keydown", this.handleEscapeKey);
    
    // Make the editor content area clickable anywhere to focus
    this.$nextTick(() => {
      const editorContent = this.$el.querySelector('.editor-content');
      if (editorContent) {
        editorContent.addEventListener('click', (e) => {
          // If clicking on empty space, focus at the end
          if (e.target === editorContent || e.target.classList.contains('ProseMirror')) {
            this.editor.commands.focus('end');
          }
        });
      }
      
      // Make character count area clickable too
      const charCountArea = this.$el.querySelector('.character-count');
      if (charCountArea) {
        charCountArea.addEventListener('click', () => {
          this.editor.commands.focus('end');
        });
      }
    });
  },
  watch: {
    value(newValue) {
      if (!this.editor || this.editor.isDestroyed) return;
      const isSame = this.editor.getHTML() === newValue;
      if (!isSame) {
        this.editor.commands.setContent(newValue, false);
        this.updateCharacterCount();
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleEscapeKey);
    if (this.editor) {
      this.editor.destroy();
    }
  },
  methods: {
    isValidUrl(string) {
      try {
        const url = new URL(string);
        return url.protocol === "http:" || url.protocol === "https:";
      } catch (_) {
        return false;
      }
    },
    handleEscapeKey(event) {
      if (event.key === "Escape" || event.keyCode === 27) {
        if (this.showLinkModal) {
          this.closeLinkModal();
        } else if (this.showImageModal) {
          this.closeImageModal();
        }
      }
    },
    updateCharacterCount() {
      if (this.editor) {
        this.characterCount = (this.editor.storage.characterCount && this.editor.storage.characterCount.characters()) || this.editor.state.doc.textContent.length;
      }
    },
    openLinkModal() {
      const previousUrl = this.editor.getAttributes("link").href;
      this.linkUrl = previousUrl || "";
      this.linkError = "";
      this.showLinkModal = true;
    },
    closeLinkModal() {
      this.showLinkModal = false;
      this.linkUrl = "";
      this.linkError = "";
    },
    insertLink() {
      if (this.linkUrl === "") {
        this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
        this.closeLinkModal();
        return;
      }
      
      if (!this.isValidUrl(this.linkUrl)) {
        this.linkError = "Please enter a valid URL (e.g., https://example.com)";
        return;
      }
      
      this.editor.chain().focus().extendMarkRange("link").setLink({ href: this.linkUrl }).run();
      this.closeLinkModal();
    },
    openImageModal() {
      this.imageUrl = "";
      this.imageError = "";
      this.showImageModal = true;
    },
    closeImageModal() {
      this.showImageModal = false;
      this.imageUrl = "";
      this.imageError = "";
    },
    insertImage() {
      if (!this.imageUrl) {
        this.imageError = "Please enter an image URL";
        return;
      }
      
      if (!this.isValidUrl(this.imageUrl)) {
        this.imageError = "Please enter a valid URL (e.g., https://example.com/image.jpg)";
        return;
      }
      
      this.editor.chain().focus().setImage({ src: this.imageUrl }).run();
      this.closeImageModal();
    },
    getCurrentFormat() {
      if (!this.editor) return "paragraph";
      if (this.editor.isActive("heading", { level: 1 })) return "heading1";
      if (this.editor.isActive("heading", { level: 2 })) return "heading2";
      if (this.editor.isActive("heading", { level: 3 })) return "heading3";
      return "paragraph";
    },
    onFormatChange(event) {
      const format = event.target.value;
      if (format === "paragraph") {
        this.editor.chain().focus().setParagraph().run();
      } else if (format.startsWith("heading")) {
        const level = parseInt(format.replace("heading", ""));
        this.editor.chain().focus().toggleHeading({ level }).run();
      }
    },
  },
};
</script>

<style scoped>
.tiptap-editor {
  border: 1px solid #ced4da;
  border-radius: 2px;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: visible;
  box-sizing: border-box;
}
.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #ced4da;
  align-items: center;
  align-content: flex-start;
  width: 100%;
  max-width: 100%;
  overflow: visible;
  box-sizing: border-box;
  min-height: auto;
  flex-shrink: 0;
}
.toolbar-btn {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  min-width: 2rem;
  min-height: 2rem;
  height: auto;
  flex: 0 0 auto;
  box-sizing: border-box;
}
.toolbar-btn:hover:not(:disabled) {
  background-color: #e9ecef;
  border-color: #adb5bd;
}
.toolbar-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.toolbar-btn.is-active {
  background-color: #036d6c;
  color: white;
  border-color: #036d6c;
}
.toolbar-select {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  padding: 6px 8px;
  font-size: 0.875rem;
  cursor: pointer;
  min-height: 2rem;
  height: auto;
  max-width: 12rem;
  box-sizing: border-box;
  flex: 0 0 auto;
}
.toolbar-select:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}
.toolbar-color-picker {
  width: 2rem;
  min-height: 2rem;
  height: auto;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  cursor: pointer;
  padding: 2px;
  flex: 0 0 auto;
  box-sizing: border-box;
}
.toolbar-color-picker:hover {
  border-color: #adb5bd;
}
.toolbar-divider {
  width: 1px;
  background-color: #dee2e6;
  margin: 0 4px;
  min-height: 1.5rem;
  flex: 0 0 auto;
  align-self: stretch;
}
.editor-content {
  min-height: 400px;
  max-height: 500px;
  cursor: text;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.editor-content:hover {
  background-color: #fafafa;
}

.editor-content:focus-within {
  background-color: #fff;
}

/deep/ .ProseMirror {
  padding: 12px;
  min-height: 400px;
  max-height: 500px;
  overflow-y: auto;
  outline: none;
  font-family: "Poppins", sans-serif;
  font-size: 0.9rem;
  color: black;
  background: #fff;
  font-weight: normal !important;
  cursor: text;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}
/deep/ .ProseMirror strong,
/deep/ .ProseMirror b {
  font-weight: bold;
}
/deep/ .ProseMirror:focus {
  outline: none;
}
/deep/ .ProseMirror p {
  margin-bottom: 0.5em;
}
/deep/ .ProseMirror h1 {
  font-size: 2em;
  font-weight: bold;
  margin-bottom: 0.5em;
}
/deep/ .ProseMirror h2 {
  font-size: 1.5em;
  font-weight: bold;
  margin-bottom: 0.5em;
}
/deep/ .ProseMirror h3 {
  font-size: 1.3em;
  font-weight: bold;
  margin-bottom: 0.5em;
}
/deep/ .ProseMirror ul,
/deep/ .ProseMirror ol {
  padding-left: 1.5em;
  margin-bottom: 0.5em;
}
/deep/ .ProseMirror a {
  color: #036d6c;
  text-decoration: underline;
  cursor: pointer;
}
/deep/ .ProseMirror a strong,
/deep/ .ProseMirror a b {
  font-weight: bold;
}
/deep/ .ProseMirror a em,
/deep/ .ProseMirror a i {
  font-style: italic;
}
/deep/ .ProseMirror a u {
  text-decoration: underline;
}
/deep/ .ProseMirror a s,
/deep/ .ProseMirror a strike {
  text-decoration: line-through;
}
/deep/ .ProseMirror a:hover {
  color: #024d4c;
}
/deep/ .ProseMirror mark {
  background-color: yellow;
  padding: 2px 0;
}
/deep/ .ProseMirror img {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0.5em 0;
  border-radius: 3px;
}

/* Handle different zoom levels */
@media screen and (min-resolution: 144dpi),
       screen and (min-resolution: 1.5dppx) {
  .toolbar-btn {
    min-width: 1.75rem;
    height: 1.75rem;
  }
  
  .toolbar-select {
    height: 1.75rem;
  }
  
  .toolbar-color-picker {
    width: 1.75rem;
    height: 1.75rem;
  }
}

@media (max-width: 768px) {
  .toolbar-btn {
    min-width: 2rem;
    height: 2rem;
    font-size: 0.8125rem;
  }
  
  .toolbar-select {
    min-width: 6rem;
    font-size: 0.8125rem;
  }
  
  .toolbar-color-picker {
    width: 2rem;
    height: 2rem;
  }
  
  .toolbar-divider {
    height: 1.25rem;
  }
  
  .editor-toolbar {
    gap: 4px;
  }
}

/* Force no horizontal scroll on toolbar - always wrap instead */
.editor-toolbar {
  overflow-x: visible !important;
  overflow-y: visible !important;
  min-width: 0 !important;
}

.tiptap-editor {
  overflow-x: visible !important;
}

/* Ensure all toolbar elements can wrap properly */
.editor-toolbar > * {
  flex-shrink: 0;
  flex-grow: 0;
  flex-basis: auto;
}

.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
}

.custom-modal {
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 500px;
}

.custom-modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.custom-modal-header h5 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #000;
  opacity: 0.5;
  padding: 0;
  width: 30px;
  height: 30px;
  line-height: 1;
}

.close-btn:hover {
  opacity: 0.75;
}

.custom-modal-body {
  padding: 20px;
}

.custom-modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.form-control {
  display: block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  color: #495057;
  background-color: #fff;
  border-color: #036d6c;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(3, 109, 108, 0.25);
}

.btn {
  display: inline-block;
  font-weight: 400;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
  user-select: none;
  border: 1px solid transparent;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: 0.25rem;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  cursor: pointer;
}

.btn-secondary {
  color: #fff;
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover {
  background-color: #5a6268;
  border-color: #545b62;
}

.btn-primary {
  color: #fff;
  background-color: #036d6c;
  border-color: #036d6c;
}

.btn-primary:hover {
  background-color: #025a59;
  border-color: #024d4c;
}

.error-message {
  display: block;
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.character-count {
  padding: 8px 12px;
  text-align: right;
  font-size: 0.875rem;
  color: #6c757d;
  border-top: 1px solid #ced4da;
  background-color: #f8f9fa;
  cursor: text;
  user-select: none;
}

.character-count:hover {
  background-color: #e9ecef;
}
</style>

<style>
/* Global styles for Tiptap modals to ensure they're always visible */
.tiptap-editor .custom-modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 999999 !important;
  pointer-events: auto !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.tiptap-editor .custom-modal {
  background: white !important;
  border-radius: 4px !important;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
  width: 90% !important;
  max-width: 500px !important;
  position: relative !important;
  pointer-events: auto !important;
}
</style>
